import { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";

interface CurrentQuizProps {
  tripId: string;
  userId: string;
  tripTitle?: string;
}

interface QuizQuestion {
  id: string;
  question: string;
  choices: string[];
  correct_index: number;
  created_at: string | null;
}

interface QuizAnswer {
  question_id: string | null;
  selected_index: number | null;
  is_correct: boolean | null;
  feedback: string | null;
}

export default function CurrentQuiz({ tripId, userId, tripTitle }: CurrentQuizProps) {
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, QuizAnswer>>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [pendingAnswer, setPendingAnswer] = useState<number | null>(null);
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [nextQuestionTime, setNextQuestionTime] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadQuiz();
  }, [tripId, userId]);

  const loadQuiz = async () => {
    setLoading(true);

    const { data: allQuestions } = await supabase
      .from("trip_quiz_questions")
      .select("id, question, choices, correct_index, created_at")
      .eq("trip_id", tripId)
      .order("created_at", { ascending: true });

    if (!allQuestions || allQuestions.length === 0) {
      setLoading(false);
      return;
    }

    // Alle Antworten des Users in einer Query laden (kein N+1)
    const questionIds = allQuestions.map((q) => q.id);
    const { data: userAnswers } = await supabase
      .from("trip_quiz_answers")
      .select("question_id, selected_index, is_correct, feedback")
      .eq("user_id", userId)
      .in("question_id", questionIds);

    const answersMap: Record<string, QuizAnswer> = {};
    for (const a of userAnswers ?? []) {
      if (a.question_id) answersMap[a.question_id] = a;
    }

    setQuestions(allQuestions);
    setAnswers(answersMap);

    // Ersten unbeantworteten Index finden
    const firstUnanswered = allQuestions.findIndex((q) => !answersMap[q.id]);
    if (firstUnanswered >= 0) {
      setCurrentIndex(firstUnanswered);
    } else {
      // Alle beantwortet – Interval prüfen
      setCurrentIndex(allQuestions.length);
      const lastQuestionCreatedAt = allQuestions[allQuestions.length - 1]?.created_at;
      if (lastQuestionCreatedAt) {
        const { data: flags } = await supabase
          .from("feature_flags")
          .select("min_interval_hours")
          .eq("feature_key", "ai_quiz_game")
          .maybeSingle();
        const intervalHours = flags?.min_interval_hours != null ? Number(flags.min_interval_hours) : 24;
        const nextAt = new Date(new Date(lastQuestionCreatedAt).getTime() + intervalHours * 60 * 60 * 1000);
        if (nextAt > new Date()) {
          setNextQuestionTime(nextAt);
        }
      }
    }

    setLoading(false);
  };

  const handleAnswer = async (selectedIdx: number) => {
    const question = questions[currentIndex];
    if (!question) return;

    const correct = selectedIdx === question.correct_index;
    setPendingAnswer(selectedIdx);

    const newAnswer: QuizAnswer = {
      question_id: question.id,
      selected_index: selectedIdx,
      is_correct: correct,
      feedback: null,
    };

    await supabase.from("trip_quiz_answers").insert({
      question_id: question.id,
      user_id: userId,
      selected_index: selectedIdx,
      is_correct: correct,
    });

    setAnswers((prev) => ({ ...prev, [question.id]: newAnswer }));
    setFeedbackGiven(false);
  };

  const handleFeedback = async (value: string) => {
    const question = questions[currentIndex];
    if (!question) return;

    await supabase
      .from("trip_quiz_answers")
      .update({ feedback: value })
      .eq("question_id", question.id)
      .eq("user_id", userId);

    setAnswers((prev) => ({
      ...prev,
      [question.id]: { ...prev[question.id], feedback: value },
    }));
    setFeedbackGiven(true);
  };

  const goNext = () => {
    setPendingAnswer(null);
    setFeedbackGiven(false);
    setCurrentIndex((prev) => prev + 1);
  };

  if (loading) return null;
  if (questions.length === 0) return null;

  const allAnswered = currentIndex >= questions.length;
  const question = questions[currentIndex];
  const existingAnswer = question ? answers[question.id] : null;
  const isAnswered = !!existingAnswer || pendingAnswer !== null;
  const selectedIdx = existingAnswer?.selected_index ?? pendingAnswer;
  const isCorrect = existingAnswer?.is_correct ?? (pendingAnswer !== null ? pendingAnswer === question?.correct_index : null);
  const currentFeedback = existingAnswer?.feedback ?? null;
  const answeredCount = Object.keys(answers).length;
  const progress = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;

  return (
    <div className="bg-[#161b27] border border-[#2a3347] rounded-xl p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-100">
          🌍 Quiz{tripTitle ? `: ${tripTitle}` : ""}
        </h3>
        <span className="text-xs text-slate-500">
          {Math.min(answeredCount, questions.length)}/{questions.length} beantwortet
        </span>
      </div>

      {/* Fortschrittsbalken */}
      <div className="h-1 bg-[#2a3347] rounded-full mb-4 overflow-hidden">
        <div
          className="h-full bg-sky-500/60 rounded-full transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Alle beantwortet */}
      {allAnswered && (
        <div className="text-center py-2">
          {nextQuestionTime ? (
            <p className="text-xs text-slate-500">
              🕓 Nächste Frage verfügbar am{" "}
              {nextQuestionTime.toLocaleString("de-CH", {
                weekday: "short",
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          ) : (
            <p className="text-sm text-emerald-400 font-medium">
              ✅ Alle Fragen beantwortet!
            </p>
          )}
        </div>
      )}

      {/* Aktuelle Frage */}
      {!allAnswered && question && (
        <>
          <p className="text-xs text-slate-500 mb-2">
            Frage {currentIndex + 1} von {questions.length}
          </p>
          <p className="text-sm text-slate-200 mb-3 leading-snug">{question.question}</p>

          <ul className="space-y-2">
            {question.choices.map((choice, idx) => {
              let btnClass =
                "w-full text-left px-3 py-2.5 rounded-lg text-sm border transition-colors ";

              if (!isAnswered) {
                btnClass +=
                  "bg-[#1e2639] text-slate-300 border-[#2a3347] hover:bg-[#2a3347] hover:border-sky-500/30 cursor-pointer";
              } else if (idx === question.correct_index) {
                btnClass +=
                  "bg-emerald-900/30 text-emerald-400 border-emerald-800 cursor-default";
              } else if (idx === selectedIdx && !isCorrect) {
                btnClass +=
                  "bg-red-950/40 text-red-400 border-red-800 cursor-default";
              } else {
                btnClass +=
                  "bg-[#0d1117] text-slate-600 border-[#2a3347] cursor-default";
              }

              return (
                <li key={idx}>
                  <button
                    className={btnClass}
                    disabled={isAnswered}
                    onClick={() => !isAnswered && handleAnswer(idx)}
                  >
                    <span className="font-medium mr-2 text-slate-500">
                      {String.fromCharCode(65 + idx)}.
                    </span>
                    {choice}
                    {isAnswered && idx === question.correct_index && (
                      <span className="ml-2 text-emerald-400">✓</span>
                    )}
                    {isAnswered && idx === selectedIdx && !isCorrect && idx !== question.correct_index && (
                      <span className="ml-2 text-red-400">✗</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Feedback nach Antwort */}
          {isAnswered && (
            <div className="mt-3 pt-3 border-t border-[#2a3347]">
              <p className="text-xs font-medium mb-2">
                {isCorrect ? (
                  <span className="text-emerald-400">✅ Richtig!</span>
                ) : (
                  <span className="text-red-400">❌ Leider falsch.</span>
                )}
              </p>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {!currentFeedback && !feedbackGiven ? (
                    <>
                      <span className="text-xs text-slate-500">Hat die Frage gefallen?</span>
                      <button
                        onClick={() => handleFeedback("👍")}
                        className="text-sm px-2 py-1 rounded bg-[#1e2639] border border-[#2a3347] hover:bg-[#2a3347] transition-colors"
                      >
                        👍
                      </button>
                      <button
                        onClick={() => handleFeedback("👎")}
                        className="text-sm px-2 py-1 rounded bg-[#1e2639] border border-[#2a3347] hover:bg-[#2a3347] transition-colors"
                      >
                        👎
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-slate-500">Danke für dein Feedback!</span>
                  )}
                </div>

                {currentIndex < questions.length - 1 && (
                  <button
                    onClick={goNext}
                    className="text-xs text-sky-400 hover:text-sky-300 transition-colors"
                  >
                    Nächste Frage →
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
